import { Flex, Icon, Center, Button, Text } from '@chakra-ui/react';
import { NotificationCard } from '../../components/Card';
import { NotificationTime } from '../../components/Time';
import { decode } from 'js-base64';
import { UsersRight } from '@kula-ai/icons/outline/UsersRight';
import { CollapsibleSection, DetailsList } from './components';
import { KText } from '@/ui-library/components';
import { ArrowRightIcon } from '@heroicons/react/outline';

const StageStatusConfig = {
  'Under review': {
    id: 'Under review',
    label: 'Under review',
    color: 'pastel-6-dark',
    bg: 'pastel-6-light',
  },
  Interviewing: {
    id: 'Interviewing',
    label: 'Interviewing',
    color: 'pastel-5-dark',
    bg: 'pastel-5-light',
  },
  Offer: {
    id: 'Offer',
    label: 'Offer',
    color: 'pastel-4-dark',
    bg: 'pastel-4-light',
  },
  Hired: {
    id: 'Hired',
    label: 'Hired',
    color: 'pastel-2-dark',
    bg: 'pastel-2-light',
  },
  'Not selected': {
    id: 'Not selected',
    label: 'Not selected',
    color: 'pastel-1-dark',
    bg: 'pastel-1-light',
  },
};

const StageStatusBadge = ({ milestone }) => {
  const { label, color, bg } = StageStatusConfig[milestone] || {
    label: milestone,
    color: 'secondary-500',
    bg: 'secondary-50',
  };
  return (
    <Text
      w="fit-content"
      p="2px 8px"
      borderRadius="xs"
      color={color}
      fontSize="sm"
      fontWeight="450"
      bg={bg}
    >
      {label}
    </Text>
  );
};

const StatusChangeContent = ({ fromStatus, toStatus }) => {
  return (
    <Flex flexDir="column" gap="8px" p="16px">
      <Flex alignItems="center" gap="8px">
        <StageStatusBadge milestone={fromStatus} />
        <Icon as={ArrowRightIcon} width="16px" color="secondary-500" />
        <StageStatusBadge milestone={toStatus} />
      </Flex>
    </Flex>
  );
};

const ReferralUpdated = ({ notification = {}, onClose = () => {} }) => {
  const { custom_attributes } = notification;
  const data = JSON.parse(decode(custom_attributes));
  const {
    application_candidate_url,
    candidate = {},
    status_change = [],
    job_details = {},
  } = data || {};

  const [fromStatus, toStatus] = status_change;

  const candidateData = [
    { label: 'Name', value: candidate?.name },
    { label: 'Email', value: candidate?.email },
    { label: 'Phone', value: candidate?.phone },
  ].filter((item) => item.value);

  const jobDetailsData = [
    job_details?.name && { label: 'Job title', value: job_details?.name },
    job_details?.department && {
      label: 'Department',
      value: job_details?.department,
    },
    job_details?.office && { label: 'Office', value: job_details?.office },
    job_details?.workplace && {
      label: 'Workplace',
      value: job_details?.workplace,
    },
    job_details?.work_type && {
      label: 'Work type',
      value: job_details?.work_type,
    },
  ].filter(Boolean);

  const handleViewReferral = () => {
    if (application_candidate_url) {
      window.open(application_candidate_url, '_self');
      onClose();
    }
  };

  return (
    <Flex alignItems="baseline" gap="6px">
      <Center minW="34px" h="34px" borderRadius="sm" bg="primary-100">
        <Icon as={UsersRight} color="primary-500" width="16px" />
      </Center>
      <Flex flexDir="column" ml="16px" gap="8px" alignSelf="start" flex="1">
        <KText>
          Your referral to{' '}
          <KText as="span" fontWeight="semiBold">
            {job_details?.name}
          </KText>{' '}
          has been updated
        </KText>

        <CollapsibleSection title="Candidate details">
          <DetailsList data={candidateData} />
        </CollapsibleSection>

        {status_change?.length === 2 && (
          <CollapsibleSection title="Status">
            <StatusChangeContent fromStatus={fromStatus} toStatus={toStatus} />
          </CollapsibleSection>
        )}

        <CollapsibleSection title="Job details">
          <DetailsList data={jobDetailsData} />
        </CollapsibleSection>

        <Button
          variant="primary"
          onClick={handleViewReferral}
          size="sm"
          w="fit-content"
          data-test-id="view-referral-button"
        >
          View referral
        </Button>

        <NotificationTime notification={notification} />
      </Flex>
    </Flex>
  );
};

export const ReferralUpdatedNotification = ({
  notification = {},
  onClose = () => {},
}) => {
  return (
    <NotificationCard notification={notification}>
      <ReferralUpdated notification={notification} onClose={onClose} />
    </NotificationCard>
  );
};
